/**
 * Popover — a light, content-agnostic anchored panel: filter stacks, column
 * pickers, the colour key on the progress heatmap.
 *
 * Where Dropdown owns a fixed list of menu items, Popover owns nothing but a
 * surface, so callers render forms and legends inside it. It is non-modal — the
 * page keeps scrolling and nothing behind it is hidden from assistive tech — but
 * Tab is still contained between the trigger and the panel so keyboard users are
 * never stranded, and Escape returns focus to the trigger. Works uncontrolled or
 * controlled through `open` / `onOpenChange`.
 */
import {
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type HTMLAttributes,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { cn } from '../../lib/cn';
import { useDismissable } from './Modal';

/** Props injected into the caller's trigger element. */
interface TriggerProps {
  onClick?: (event: ReactMouseEvent<HTMLElement>) => void;
  'aria-haspopup'?: 'dialog';
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
  'data-state'?: 'open' | 'closed';
}

export interface PopoverProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title' | 'children'> {
  /** Usually a `<Button>`; cloned so it receives the disclosure ARIA wiring. */
  trigger: ReactNode;
  children: ReactNode;
  /** Renders a header row and names the panel via `aria-labelledby`. */
  title?: ReactNode;
  /** Accessible name when no visible `title` is given. */
  label?: string;
  align?: 'start' | 'end';
  side?: 'bottom' | 'top';
  /** Controlled open state; omit to let the popover manage its own. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  panelClassName?: string;
}

export const Popover = forwardRef<HTMLDivElement, PopoverProps>(function Popover(
  {
    trigger,
    children,
    title,
    label = 'More options',
    align = 'start',
    side = 'bottom',
    open,
    onOpenChange,
    panelClassName,
    className,
    ...props
  },
  forwardedRef,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const panelId = useId();
  const headingId = useId();

  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : uncontrolledOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const close = useCallback(() => setOpen(false), [setOpen]);

  // The wrapper spans trigger and panel, so pressing the trigger to close never
  // registers as an outside click.
  useDismissable({
    isOpen,
    onClose: close,
    ref: containerRef,
    lockScroll: false,
    closeOnOutsideClick: true,
    // The trigger is the first focusable node in the wrapper, so the panel takes
    // focus itself instead — that is what announces the dialog and its label.
    autoFocus: false,
  });

  useEffect(() => {
    if (!isOpen) return;
    panelRef.current?.focus();
  }, [isOpen]);

  const triggerProps: TriggerProps = {
    onClick: () => setOpen(!isOpen),
    'aria-haspopup': 'dialog',
    'aria-expanded': isOpen,
    'aria-controls': isOpen ? panelId : undefined,
    'data-state': isOpen ? 'open' : 'closed',
  };

  /** Clone an element trigger so the caller keeps their own handler and styling. */
  function renderTrigger(): ReactNode {
    if (!isValidElement(trigger)) {
      return (
        <button
          type="button"
          {...triggerProps}
          className="inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-surface px-3 text-sm font-medium text-content shadow-xs transition-colors hover:bg-surface-sunken"
        >
          {trigger}
        </button>
      );
    }

    const element = trigger as ReactElement<TriggerProps>;
    return cloneElement(element, {
      ...triggerProps,
      onClick: (event: ReactMouseEvent<HTMLElement>) => {
        element.props.onClick?.(event);
        if (!event.defaultPrevented) setOpen(!isOpen);
      },
    });
  }

  return (
    <div
      ref={(node) => {
        containerRef.current = node;
        if (typeof forwardedRef === 'function') forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      }}
      className={cn('relative inline-block text-left', className)}
      {...props}
    >
      {renderTrigger()}

      {isOpen ? (
        <div
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-labelledby={title ? headingId : undefined}
          aria-label={title ? undefined : label}
          tabIndex={-1}
          className={cn(
            'absolute z-40 min-w-[14rem] rounded-xl border border-border bg-surface-raised p-4 shadow-popover animate-scale-in',
            side === 'bottom' ? 'top-full mt-2' : 'bottom-full mb-2',
            align === 'end' ? 'right-0 origin-top-right' : 'left-0 origin-top-left',
            panelClassName,
          )}
        >
          {title ? (
            <h3 id={headingId} className="mb-3 text-sm font-medium text-content">
              {title}
            </h3>
          ) : null}
          {children}
        </div>
      ) : null}
    </div>
  );
});
